import { Link } from 'react-router-dom';
import { LandingNav, LandingFooter } from './LandingShell';
import { Section, H3, Callout, Tr } from './PolicyComponents';

const EFFECTIVE_DATE = 'March 23, 2026';

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-surface-base-light dark:bg-surface-base-dark">
      <LandingNav />
      <main className="max-w-3xl mx-auto px-6 py-12">
        <header className="mb-10 pb-8 border-b border-border-subtle-light dark:border-border-subtle-dark">
          <h1 className="text-3xl font-bold text-text-primary-light dark:text-text-primary-dark tracking-tight mb-2">
            Privacy Policy
          </h1>
          <p className="text-sm text-text-tertiary-light dark:text-text-tertiary-dark">
            Effective Date: {EFFECTIVE_DATE}
          </p>
        </header>

        <div className="prose space-y-10 text-text-primary-light dark:text-text-primary-dark text-[15px] leading-relaxed">

          {/* 1. Introduction */}
          <Section id="introduction" title="1. Introduction">
            <p>
              This Privacy Policy (&ldquo;Policy&rdquo;) describes how The Craft Lab LLC
              (&ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;)
              collects, uses, stores, and discloses information when you access or use the Danny
              Tasks application, website, browser extension, API, CLI, and all related services
              (collectively, the &ldquo;Services&rdquo;).
            </p>
            <Callout>
              BY ACCESSING OR USING THE SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD,
              AND AGREE TO BE BOUND BY THIS POLICY. IF YOU DO NOT AGREE, YOU MUST IMMEDIATELY STOP
              USING THE SERVICES.
            </Callout>
            <p>
              We reserve the right to modify this Policy at any time. Your continued use of the
              Services after any changes constitutes acceptance of the revised Policy. It is your
              responsibility to review this Policy periodically.
            </p>
          </Section>

          {/* 2. Information We Collect */}
          <Section id="information-we-collect" title="2. Information We Collect">
            <H3>2.1 Account Information</H3>
            <p>When you create an account, we collect:</p>
            <ul>
              <li>Email address</li>
              <li>Name (if provided or obtained via OAuth)</li>
              <li>Profile image URL (from OAuth providers)</li>
              <li>Hashed password (for email/password authentication)</li>
              <li>OAuth tokens and provider identifiers (Google, GitHub)</li>
            </ul>

            <H3>2.2 Task and Productivity Data</H3>
            <p>When you use the Services, we collect and process:</p>
            <ul>
              <li>Task content, descriptions, labels, priorities, due dates, and project assignments</li>
              <li>Task completion history and timestamps</li>
              <li>AI-generated metadata including categories, time estimates, energy levels, confidence scores, and AI reasoning</li>
              <li>Semantic vector embeddings derived from your task content</li>
              <li>Productivity insights generated from your usage patterns</li>
              <li>Custom dashboard views and filter configurations</li>
            </ul>

            <H3>2.3 Integration Data</H3>
            <p>If you connect third-party services:</p>
            <ul>
              <li><strong>Todoist:</strong> API key (encrypted), sync tokens, and task data imported from your Todoist account</li>
              <li><strong>Google:</strong> OAuth access and refresh tokens for Calendar and Gmail integration</li>
              <li><strong>GitHub:</strong> OAuth tokens for authentication</li>
            </ul>

            <H3>2.4 Usage and Telemetry Data</H3>
            <p>We automatically collect:</p>
            <ul>
              <li>Pages viewed, features used, and interaction events</li>
              <li>API request metadata (endpoints, methods, status codes, latency, request/response sizes)</li>
              <li>Session information including IP address and user agent</li>
              <li>Error reports and stack traces</li>
              <li>A/B test and feature flag assignments</li>
            </ul>

            <H3>2.5 AI Processing Data</H3>
            <p>When you use AI-powered features, we collect and retain:</p>
            <ul>
              <li>Full prompts sent to AI providers (including your task content and context)</li>
              <li>Complete AI responses received</li>
              <li>Token usage counts, model identifiers, processing latency</li>
              <li>Cost calculations associated with your AI usage</li>
            </ul>

            <H3>2.6 Billing Data</H3>
            <ul>
              <li>Subscription tier and trial status</li>
              <li>Stripe customer identifier</li>
              <li>Per-period usage metrics (API requests, task operations, AI token costs)</li>
              <li>Billing amounts and invoice references</li>
            </ul>

            <H3>2.7 Browser Extension Data</H3>
            <p>The browser extension may access:</p>
            <ul>
              <li>Active tab URLs (when side panel is opened)</li>
              <li>Clipboard content (when you initiate copy actions)</li>
              <li>Local extension storage for preferences</li>
            </ul>
          </Section>

          {/* 3. How We Use Your Information */}
          <Section id="how-we-use-information" title="3. How We Use Your Information">
            <p>We use collected information to:</p>
            <ul>
              <li>Provide, maintain, and improve the Services</li>
              <li>Process your tasks through AI classification, estimation, and embedding</li>
              <li>Generate personalized productivity insights</li>
              <li>Process payments and manage subscriptions</li>
              <li>Send transactional emails (verification, password reset)</li>
              <li>Monitor for errors, abuse, and security incidents</li>
              <li>Conduct analytics to understand usage patterns and improve features</li>
              <li>Enforce our terms and comply with legal obligations</li>
            </ul>
            <Callout>
              We may create anonymized, aggregated, or de-identified data sets from information we
              collect. Such data is not subject to this Policy and may be used for any lawful purpose,
              including improving our products, conducting research, and sharing with third parties.
            </Callout>
          </Section>

          {/* 4. Third-Party Services */}
          <Section id="third-party-services" title="4. Third-Party Services and Data Sharing">
            <Callout>
              Your data is shared with the following third-party services when you use the
              corresponding features. We are not responsible for the privacy practices of these
              third parties, and you accept all risks associated with their processing of your data.
            </Callout>

            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border-collapse">
                <caption className="sr-only">
                  Third-party services we use and the data shared with them, including the purpose of processing.
                </caption>
                <thead>
                  <tr className="border-b border-border-subtle-light dark:border-border-subtle-dark">
                    <th scope="col" className="text-left py-2 pr-4 font-semibold">Service</th>
                    <th scope="col" className="text-left py-2 pr-4 font-semibold">Data Shared</th>
                    <th scope="col" className="text-left py-2 font-semibold">Purpose</th>
                  </tr>
                </thead>
                <tbody className="text-text-secondary-light dark:text-text-secondary-dark">
                  <Tr cells={['Anthropic (Claude)', 'Task content, prompts, context', 'AI classification, estimation, chat']} />
                  <Tr cells={['OpenAI', 'Task content', 'Semantic embeddings']} />
                  <Tr cells={['Google', 'OAuth credentials', 'Authentication, Calendar/Gmail']} />
                  <Tr cells={['GitHub', 'OAuth credentials', 'Authentication']} />
                  <Tr cells={['Stripe', 'Customer ID, billing events', 'Payment processing']} />
                  <Tr cells={['Mailjet', 'Email address, token URLs', 'Transactional email']} />
                  <Tr cells={['Statsig', 'Anonymized user ID, events', 'Analytics and feature flags']} />
                  <Tr cells={['Sentry', 'Error traces, context', 'Error monitoring']} />
                  <Tr cells={['Neon', 'All stored data', 'Database hosting']} />
                  <Tr cells={['Google Cloud Run (GCP)', 'All transmitted data', 'Application hosting']} />
                  <Tr cells={['Todoist', 'API credentials, sync tokens', 'Task synchronization']} />
                </tbody>
              </table>
            </div>

            <p>
              We may also disclose your information: (a)&nbsp;to comply with applicable law,
              regulation, or legal process; (b)&nbsp;to enforce our agreements; (c)&nbsp;to protect
              the rights, safety, or property of us, our users, or the public; (d)&nbsp;in connection
              with a merger, acquisition, or sale of assets; or (e)&nbsp;with your consent.
            </p>
          </Section>

          {/* 5. AI Disclaimers */}
          <Section id="ai-disclaimers" title="5. AI-Specific Disclaimers">
            <Callout>
              AI-GENERATED CONTENT IS PROVIDED &ldquo;AS IS&rdquo; WITHOUT ANY WARRANTY OF
              ACCURACY, COMPLETENESS, OR FITNESS FOR ANY PURPOSE.
            </Callout>
            <p>
              We do not guarantee that AI-generated classifications, time estimates, priority scores,
              insights, or any other AI output will be accurate, reliable, or suitable for your needs.
              You are solely responsible for reviewing and acting upon any AI-generated content.
            </p>
            <p>
              Task data sent to AI providers (Anthropic, OpenAI) is processed according to their
              respective terms and privacy policies. We do not control how these providers process,
              retain, or use data once transmitted. You acknowledge and accept the risks of
              transmitting your task content to these external AI services, including the possibility
              that such providers may retain or use your data for model improvement as permitted by
              their own policies.
            </p>
          </Section>

          {/* 6. Data Storage and Security */}
          <Section id="data-storage-security" title="6. Data Storage and Security">
            <p>
              Your data is stored in PostgreSQL databases hosted by our infrastructure provider. Data
              may be processed and stored in the United States or other jurisdictions where our
              service providers operate.
            </p>
            <p>
              We implement commercially reasonable security measures, including encryption of
              sensitive credentials, HTTPS transport, secure session management, and hashed
              passwords.
            </p>
            <Callout>
              HOWEVER, NO METHOD OF TRANSMISSION OR STORAGE IS 100% SECURE. WE CANNOT AND DO NOT
              GUARANTEE THE ABSOLUTE SECURITY OF YOUR DATA. YOU ACKNOWLEDGE THAT YOU TRANSMIT AND
              STORE DATA AT YOUR OWN RISK.
            </Callout>
          </Section>

          {/* 7. Data Retention */}
          <Section id="data-retention" title="7. Data Retention">
            <p>
              We retain your data for as long as your account is active or as needed to provide the
              Services. After account deletion, we may retain data as necessary to: (a)&nbsp;comply
              with legal obligations; (b)&nbsp;resolve disputes; (c)&nbsp;enforce our agreements;
              (d)&nbsp;maintain security and prevent fraud; or (e)&nbsp;fulfill any other legitimate
              business purpose.
            </p>
            <p>
              AI interaction logs, usage metrics, and billing records may be retained for audit,
              compliance, and dispute resolution purposes even after account termination. Anonymized
              or aggregated data may be retained indefinitely.
            </p>
          </Section>

          {/* 8. Your Choices */}
          <Section id="your-choices" title="8. Your Choices">
            <ul>
              <li>
                <strong>AI Features:</strong> You may choose not to use AI-powered features. Without
                AI features enabled, task content is not sent to external AI providers.
              </li>
              <li>
                <strong>Integrations:</strong> Third-party integrations (Todoist, Google, GitHub) are
                optional. Disconnecting an integration stops future data sharing with that service but
                does not retroactively delete data already shared.
              </li>
              <li>
                <strong>Account Deletion:</strong> You may request account deletion by contacting us.
                Upon deletion, we will make commercially reasonable efforts to delete your personal
                data, subject to the retention provisions in Section&nbsp;7.
              </li>
              <li>
                <strong>Analytics:</strong> Certain analytics and telemetry are integral to the
                operation of the Services and cannot be individually disabled.
              </li>
            </ul>
          </Section>

          {/* 9. Your Rights */}
          <Section id="your-rights" title="9. Your Rights">
            <p>
              Depending on your jurisdiction, you may have rights regarding your personal data,
              including rights of access, correction, deletion, portability, and objection to
              processing. To exercise these rights, contact us at the address in Section&nbsp;14.
            </p>
            <p>
              We will respond to valid requests within the timeframe required by applicable law. We
              may request verification of your identity before processing requests. We reserve the
              right to deny requests that are manifestly unfounded, excessive, or not required by
              applicable law.
            </p>
            <p>
              <strong>For users in the EEA/UK:</strong> Our legal bases for processing are:
              (a)&nbsp;performance of a contract (providing the Services); (b)&nbsp;legitimate
              interests (analytics, security, fraud prevention); and (c)&nbsp;consent (where
              applicable for optional features). You have the right to lodge a complaint with your
              local data protection authority.
            </p>
          </Section>

          {/* 10. Children's Privacy */}
          <Section id="children" title="10. Children&rsquo;s Privacy">
            <p>
              The Services are not directed to individuals under the age of 13 (or under 16 in the
              EEA/UK). We do not knowingly collect personal information from children. If we learn
              that we have collected personal information from a child, we will take steps to delete
              such information. If you believe a child has provided us with personal information,
              contact us immediately.
            </p>
          </Section>

          {/* 11. Cookies */}
          <Section id="cookies" title="11. Cookies and Tracking Technologies">
            <p>
              We use essential cookies for authentication and session management. We use local
              storage for UI preferences and application state. We do not use third-party advertising
              cookies or cross-site tracking.
            </p>
          </Section>

          {/* 12. Limitation of Liability */}
          <Section id="limitation-of-liability" title="12. Limitation of Liability">
            <Callout>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE COMPANY SHALL NOT BE LIABLE FOR
              ANY UNAUTHORIZED ACCESS TO, ALTERATION OF, OR LOSS, DESTRUCTION, OR DISCLOSURE OF YOUR
              DATA, WHETHER THROUGH BREACH, NEGLIGENCE, OR ANY OTHER CAUSE. IN NO EVENT SHALL OUR
              TOTAL LIABILITY ARISING OUT OF OR RELATED TO THIS POLICY OR YOUR USE OF THE SERVICES
              EXCEED THE AMOUNTS YOU HAVE PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM,
              OR FIFTY DOLLARS ($50), WHICHEVER IS GREATER.
            </Callout>
          </Section>

          {/* 13. Indemnification */}
          <Section id="indemnification" title="13. Indemnification">
            <p>
              You agree to indemnify, defend, and hold harmless the Company, its officers, directors,
              employees, and agents from and against any claims, liabilities, damages, losses, and
              expenses (including reasonable attorneys&rsquo; fees) arising out of or in any way
              connected with: (a)&nbsp;your use of the Services; (b)&nbsp;your violation of this
              Policy; (c)&nbsp;your violation of any rights of another party; or (d)&nbsp;any content
              you submit through the Services.
            </p>
          </Section>

          {/* 14. Contact */}
          <Section id="contact" title="14. Contact">
            <p>
              For questions about this Policy or to exercise your data rights, contact:
            </p>
            <p>
              <strong>The Craft Lab LLC</strong><br />
              Email: privacy@askdanny.to
            </p>
          </Section>

          {/* 15. Dispute Resolution */}
          <Section id="dispute-resolution" title="15. Dispute Resolution">
            <p>
              Any dispute arising out of or relating to this Policy shall be resolved by binding
              individual arbitration under the rules of the American Arbitration Association.
            </p>
            <Callout>
              YOU WAIVE ANY RIGHT TO PARTICIPATE IN A CLASS ACTION OR CLASS ARBITRATION.
            </Callout>
            <p>
              The arbitration shall take place in the state of the Company&rsquo;s principal place of
              business. Each party shall bear its own costs.
            </p>
          </Section>

          {/* 16. Severability */}
          <Section id="severability" title="16. Severability">
            <p>
              If any provision of this Policy is held invalid or unenforceable, the remaining
              provisions shall remain in full force and effect.
            </p>
          </Section>
        </div>

        <footer className="mt-16 pt-8 border-t border-border-subtle-light dark:border-border-subtle-dark text-center">
          <Link
            to="/"
            className="text-sm text-primary-500 hover:text-primary-400 transition-colors"
          >
            Back to Home
          </Link>
        </footer>
      </main>
      <LandingFooter />
    </div>
  );
}
